import React, { useEffect, useState } from 'react';
import {
  FiltersType,
  useQuery,
  useNavigate,
  useParams,
  useLocation,
} from '@common/hooks';
import {
  AppContent,
  ErrorHandler,
  Flex,
  Gap,
  Gutter,
  Icon,
  Link,
  LoadingState,
  SecondaryButton,
  StatePill,
  Tabs,
  Title,
} from '@common/components';
import { OnDemandTeamPageActions, WorkerForm } from '@scheduling/components';
import { setBreadcrumbs } from 'works/legacy/lib/client-store';
import { ORGANIZATION_ADMIN_MENU } from 'works/pages/organization/admin-settings/organization-admin-vertical-nav-menu';
import {
  AdminWorkforceGroupDetails,
  AdminWorkforceGroupLocations,
  AdminWorkforceGroupStaff,
} from '@scheduling/pages/organization';
import { AdminWorkforceGroupRates } from './admin-workforce-group-rates';
import { Button } from 'antd';
import { WorkforceGroupById } from '@scheduling/graphql';
import { buildDialogUrl } from '@common/services';
import { useCurrentMembership } from '@identity/hooks';
import { RATES_URLS } from '@institutions/services';

export function AdminWorkforceGroupPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { workforceGroupId, tabKey } = useParams<{
    workforceGroupId: string;
    tabKey: 'details' | 'staff' | 'rates';
  }>();

  const [showNewStaffDrawer, setShowNewStaffDrawer] = useState(false);
  const [currentTableFilters, setCurrentTableFilters] = useState<FiltersType>(
    {}
  );

  const exportFilters =
    tabKey === 'staff'
      ? {
          ...currentTableFilters,
          workforceGroup: [workforceGroupId],
          active: ['true'],
        }
      : undefined;

  const { data, loading, error } = useQuery(WorkforceGroupById, {
    variables: { workforceGroupId },
  });

  const {
    canCreateWorkers,
    canUploadWorkers,
    canCreateWorkforceGroupUnitRate,
  } = useCurrentMembership();

  useEffect(() => {
    setBreadcrumbs([
      {
        name: 'Admin',
        path: '/works/admin',
      },
      {
        name: 'Groups',
        path: ORGANIZATION_ADMIN_MENU.GROUPS.ROUTE,
      },
      {
        name: data?.workforceGroup.name || '',
        path: `${ORGANIZATION_ADMIN_MENU.GROUPS.ROUTE}/${workforceGroupId}/details`,
      },
    ]);
  }, [data?.workforceGroup.name, workforceGroupId]);

  if (error) {
    return <ErrorHandler error={error} log="admin-workforce-group-page" />;
  }

  if (!data || loading) {
    return (
      <AppContent>
        <LoadingState />
      </AppContent>
    );
  }

  const handleTabChange = (key: string) =>
    navigate(location.pathname.replace(`/${tabKey}`, `/${key}`));

  return (
    <AppContent>
      <Gutter size="sm" direction="vertical">
        <Flex align="center" justify="space-between" fullWidth>
          <Gap size="md" align="center">
            <Title size="h2" weight="bold">
              {data.workforceGroup.name}
            </Title>
            <StatePill
              color={data.workforceGroup.active ? 'grass' : 'steel-lighter'}
            >
              {data.workforceGroup.active ? 'Active' : 'Archived'}
            </StatePill>
            <Link
              to={buildDialogUrl('workforce-group-update-drawer', {
                props: {
                  workforceGroupId,
                },
              })}
            >
              <Button type="link" id="update-button" style={{ padding: 0 }}>
                <Icon icon="pencil" size="md" />
              </Button>
            </Link>
          </Gap>

          {tabKey === 'staff' && (
            <OnDemandTeamPageActions
              loading={loading}
              disabled={!data?.workforceGroup?.active}
              canCreateWorkers={canCreateWorkers}
              canUploadWorkers={canUploadWorkers}
              onAddStaffButtonClick={() => setShowNewStaffDrawer(true)}
              exportButton
              exportFilters={exportFilters}
            />
          )}

          {tabKey === 'rates' && canCreateWorkforceGroupUnitRate && (
            <Link
              to={`${RATES_URLS.INCENTIVES}/update?workforceGroupId=${workforceGroupId}`}
            >
              <SecondaryButton>Update Rates</SecondaryButton>
            </Link>
          )}
        </Flex>
      </Gutter>
      <Tabs
        activeKey={tabKey}
        defaultActiveKey={tabKey}
        onTabClick={handleTabChange}
      >
        <Tabs.Pane tab="Details" key="details">
          <AdminWorkforceGroupDetails data={data} />
        </Tabs.Pane>
        <Tabs.Pane tab="Locations" key="locations">
          <AdminWorkforceGroupLocations
            workforceGroupId={data?.workforceGroup.id}
          />
        </Tabs.Pane>
        <Tabs.Pane tab="Staff" key="staff">
          <AdminWorkforceGroupStaff
            workforceGroupId={data?.workforceGroup.id}
            onFiltersChange={setCurrentTableFilters}
          />
        </Tabs.Pane>
        <Tabs.Pane tab="Rates" key="rates">
          <AdminWorkforceGroupRates
            workforceGroupId={data?.workforceGroup.id}
            onFiltersChange={setCurrentTableFilters}
          />
        </Tabs.Pane>
      </Tabs>
      {showNewStaffDrawer && (
        <WorkerForm
          setShowDrawer={setShowNewStaffDrawer}
          defaultValues={{ workforceGroupId: workforceGroupId }}
        />
      )}
    </AppContent>
  );
}
